/**
 * 内存监控采样锚点。
 *
 * 内存监控的第一性原理：单点读数没有意义，增量才有意义。
 * 因此采样必须锚定在生命周期事件上（而非定时轮询），
 * 这样不同用户、不同对话的样本才能在数仓侧对齐、聚合、做分位数。
 */
export const MEMORY_SAMPLE_ANCHOR = {
  /** app 挂载且首个 sandbox live 后打一次，作为该 session 的分母 */
  Baseline: 'baseline',
  /** 流式输出结束（isStreaming true→false）时打一次，代表本轮峰值 */
  ChatEnd: 'chat_end',
  /** chat_end 之后固定延迟再打一次，代表回落值；idle_settle - baseline ≈ 常驻增量（泄漏信号） */
  IdleSettle: 'idle_settle',
  /** 页面隐藏/卸载时用 beacon 兜底 */
  Unload: 'unload',
  /** 单位时间内内存涨幅超过阈值时上报（覆盖「进工作台不发对话但内存爬升」的盲区） */
  GrowthAnomaly: 'growth_anomaly',
} as const;

export type MemorySampleAnchor = (typeof MEMORY_SAMPLE_ANCHOR)[keyof typeof MEMORY_SAMPLE_ANCHOR];

/** 上报事件名（collect 会自动加 eventPrefix） */
export const MEMORY_SAMPLE_EVENT = 'memory_sample';

/**
 * chat_end 到 idle_settle 的延迟。
 * 取 15s 是为了给 GC 和异步资源释放留足时间，让回落值尽量反映真实常驻内存。
 * 期间若重新进入流式，则取消这次 idle_settle（说明用户在连续对话，回落无意义）。
 */
export const IDLE_SETTLE_DELAY_MS = 15_000;

/**
 * 同一锚点两次采样的最小间隔，防止异常抖动（如快速连发对话）导致样本爆量。
 * 主 frame 的 performance.memory 是同步读取、成本极低，这里的闸主要用于控制上报量而非性能。
 */
export const MIN_SAMPLE_INTERVAL_MS = 3_000;

/**
 * 空闲增长异常检测的 tick 间隔。
 * 只静默读数、不上报；60s 足够密地捕捉飙升，又不会灌爆埋点。
 * 仅在 document 可见且 sandbox live 时才跑，tab 隐藏即暂停，避免后台噪音。
 */
export const GROWTH_TICK_MS = 60_000;

/**
 * 触发 growth_anomaly 上报的涨幅阈值（MB/min）。
 * 结合实际内存量级（动辄 1GB）设为 100MB/min——低于此的平稳增长不上报，
 * 只在真正快速飙升时才产生一条异常样本，保证「以异常上报为主、噪音低」。
 * 上线后按数据回调。
 */
export const GROWTH_ANOMALY_THRESHOLD_MB_PER_MIN = 100;

/** MB 换算，performance.memory 单位是字节 */
export const BYTES_PER_MB = 1024 * 1024;

export type SandboxMemoryPullStatus =
  | 'ok'
  | 'pull_disabled'
  | 'provider_missing'
  | 'target_not_live'
  | 'iframe_missing'
  | 'iframe_not_registered'
  | 'rpc_timeout'
  | 'rpc_error';

export function classifySandboxMemoryPullError(error: unknown): SandboxMemoryPullStatus {
  const message = error instanceof Error ? error.message : String(error);
  const normalizedMessage = message.toLowerCase();
  if (normalizedMessage.includes('timeout')) {
    return 'rpc_timeout';
  }
  if (normalizedMessage.includes('not registered')) {
    return 'iframe_not_registered';
  }
  return 'rpc_error';
}

/** performance.memory 读数（Chromium only，非 Chromium 为 null） */
export interface JsHeapSnapshot {
  usedJsHeap: number;
  totalJsHeap: number;
  jsHeapLimit: number;
}

/** DOM 计数快照，作为节点/detached DOM 泄漏的代理指标 */
export interface DomSnapshot {
  domNodes: number;
  domIframes: number;
  domCanvases: number;
}

/** 采样时的动作上下文，决定能否定位到「哪个对话、哪个场景」 */
export interface MemorySampleContext {
  anchor: MemorySampleAnchor;
  projectId: string;
  /** 当前轮次最后一条 user 消息 id，定位到具体那一轮对话 */
  messageId: string;
  /** session 内对话轮次，用于看累积（第 1 轮 vs 第 N 轮回落值） */
  roundIndex: number;
  /** live / stable，分场景切片 */
  targetLayer: string;
  /** sandbox 存活状态，关联重载相关泄漏 */
  sandboxRuntimeStatus: string;
  /** 部署环境，用于按 cohort 对比（自动化「dev vs cleanup」这类手动对比） */
  appEnv: string;
}
